/**
 * The dialog that asks which part of a long track to load.
 *
 * A manual load of anything over `MANUAL_REGION_SECONDS` comes through here
 * instead of decoding the whole file. The waveform is the peak data the server
 * already computed, so opening this costs one small request and no decode —
 * the expensive part only happens once a region has been chosen.
 *
 * All the arithmetic lives in `region.ts` and is tested there. What is here is
 * pointers, pixels and focus.
 */

import type { Track } from "../api.js";
import { decodedBytes } from "../audio/window.js";
import type { PeakData } from "../waveform/peaks.js";
import { fitToDisplay, renderOverview, THEME } from "../waveform/render.js";
import { duration as formatDuration, escapeHtml } from "./format.js";
import {
  clampRegion,
  defaultRegion,
  MANUAL_REGION_SECONDS,
  regionEnd,
  snapToBar,
  type Region,
} from "./region.js";
import type { Bounds } from "../automix/tails.js";

/** How near an edge, in CSS pixels, a press counts as grabbing the handle. */
const EDGE_GRAB = 9;

/** Arrow keys move the region by this much; with shift, by a minute. */
const NUDGE_SECONDS = 10;
const NUDGE_COARSE_SECONDS = 60;

/** Orientation marks down the waveform. */
const GRID_SECONDS = 300;

type Drag = "move" | "start" | "end";

export interface RegionPick {
  track: Track;
  peaks: PeakData;
  bounds: Bounds;
  beats: readonly number[];
  cap?: number;
  beatsPerBar?: number;
}

/**
 * Show the picker and resolve with the chosen region, or null if cancelled.
 *
 * Cancelling loads nothing — it deliberately does not fall through to loading
 * the whole track. If the answer to "which twenty minutes?" is "all of it",
 * that is a different request from the one this dialog is asking about.
 */
export function pickRegion(request: RegionPick): Promise<Region | null> {
  return new Promise((resolve) => new RegionPicker(request, resolve));
}

class RegionPicker {
  private readonly root: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly duration: number;
  private readonly cap: number;
  private readonly beatsPerBar: number;
  private region: Region;

  /** Waveform drawn twice, so the chosen region can read brighter. */
  private dim: HTMLCanvasElement | null = null;
  private bright: HTMLCanvasElement | null = null;
  private cacheKey = "";

  private drag: { kind: Drag; pointerId: number; grabOffset: number } | null = null;
  private readonly restoreFocus: Element | null;
  private settled = false;

  constructor(
    private readonly request: RegionPick,
    private readonly resolve: (region: Region | null) => void,
  ) {
    this.duration = request.track.duration ?? 0;
    this.cap = request.cap ?? MANUAL_REGION_SECONDS;
    this.beatsPerBar = request.beatsPerBar ?? 4;
    this.region = defaultRegion(
      request.bounds,
      this.duration,
      request.beats,
      this.cap,
      this.beatsPerBar,
    );

    this.restoreFocus = document.activeElement;
    this.root = document.createElement("div");
    this.root.className = "region-gate";
    this.root.innerHTML = this.chrome();
    document.body.appendChild(this.root);

    this.canvas = this.el<HTMLCanvasElement>(".region-wave");
    this.bind();
    this.render();
    this.el<HTMLButtonElement>('[data-region="confirm"]').focus();
  }

  private chrome(): string {
    const { track } = this.request;
    const artist = track.artist ? `${escapeHtml(track.artist)} — ` : "";
    const name = escapeHtml(track.title ?? track.path);

    return `
      <div class="region-box" role="dialog" aria-modal="true" tabindex="-1"
           aria-label="Choose a region to load">
        <div class="region-head">
          <div class="region-title">${artist}${name}</div>
          <div class="muted">
            ${formatDuration(this.duration)} — too long to load whole.
            Drag out up to ${formatDuration(this.cap)} to play.
          </div>
        </div>

        <canvas class="region-wave" height="120"></canvas>

        <div class="region-foot">
          <div class="region-readout num" aria-live="polite"></div>
          <span class="spacer"></span>
          <button type="button" data-region="cancel">Cancel</button>
          <button type="button" data-region="confirm">Load region</button>
        </div>
        <div class="region-hint muted">
          Drag the band to move it, its edges to resize.
          Arrows nudge, shift+arrows by a minute. Enter loads, Esc cancels.
        </div>
      </div>
    `;
  }

  private el<T extends Element = HTMLElement>(selector: string): T {
    const node = this.root.querySelector(selector);
    if (!node) throw new Error(`missing ${selector} in region picker`);
    return node as T;
  }

  private bind(): void {
    this.el('[data-region="cancel"]').addEventListener("click", () => this.close(null));
    this.el('[data-region="confirm"]').addEventListener("click", () => this.close(this.region));

    // A press on the backdrop is a cancel, the same as Esc. A press inside the
    // box is not, or dragging the band would dismiss the dialog under the hand.
    this.root.addEventListener("pointerdown", (event) => {
      if (event.target === this.root) this.close(null);
    });

    // On document, in the capture phase, because the app binds its transport
    // shortcuts to document itself — without this, "1" or "a" while the dialog
    // is open would start a deck playing behind it. A modal owns the keyboard
    // while it is up, so every key is stopped here whether or not it is used.
    document.addEventListener("keydown", this.onKey, true);

    this.canvas.addEventListener("pointerdown", (event) => this.onPointerDown(event));
    this.canvas.addEventListener("pointermove", (event) => this.onPointerMove(event));
    this.canvas.addEventListener("pointerup", () => this.endDrag());
    this.canvas.addEventListener("pointercancel", () => this.endDrag());
    window.addEventListener("resize", this.onResize);
  }

  private readonly onResize = (): void => this.render();

  private readonly onKey = (event: KeyboardEvent): void => {
    event.stopPropagation();

    if (event.key === "Escape") {
      event.preventDefault();
      this.close(null);
      return;
    }
    // Enter confirms, except on a button, where it is that button's business.
    if (event.key === "Enter" && !(event.target instanceof HTMLButtonElement)) {
      event.preventDefault();
      this.close(this.region);
      return;
    }
    if (event.key === "Tab") {
      this.cycleFocus(event);
      return;
    }

    const step = event.shiftKey ? NUDGE_COARSE_SECONDS : NUDGE_SECONDS;
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      const direction = event.key === "ArrowLeft" ? -1 : 1;
      this.setRegion({ ...this.region, start: this.region.start + direction * step });
    }
  };

  /** Keep tabbing inside the dialog: behind it is a whole app to get lost in. */
  private cycleFocus(event: KeyboardEvent): void {
    const focusable = Array.from(this.root.querySelectorAll<HTMLElement>("button"));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Track-time second under a pointer, from CSS pixels rather than backing ones. */
  private timeAt(event: PointerEvent): number {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    return ((event.clientX - rect.left) / rect.width) * this.duration;
  }

  private secondsPerPixel(): number {
    const width = this.canvas.getBoundingClientRect().width;
    return width > 0 ? this.duration / width : 0;
  }

  private onPointerDown(event: PointerEvent): void {
    const time = this.timeAt(event);
    const grab = EDGE_GRAB * this.secondsPerPixel();

    let kind: Drag;
    if (Math.abs(time - this.region.start) <= grab) kind = "start";
    else if (Math.abs(time - regionEnd(this.region)) <= grab) kind = "end";
    else kind = "move";

    // Pressing well outside the band means "put it here" rather than a drag
    // from wherever it happens to be.
    if (kind === "move" && (time < this.region.start || time > regionEnd(this.region))) {
      this.setRegion({ ...this.region, start: time - this.region.length / 2 });
    }

    this.drag = { kind, pointerId: event.pointerId, grabOffset: time - this.region.start };
    this.canvas.setPointerCapture(event.pointerId);
    event.preventDefault();
    // A canvas cannot hold focus, so without this a press would drop focus to
    // the body and leave Tab walking the app behind the dialog.
    this.el(".region-box").focus();
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.drag || this.drag.pointerId !== event.pointerId) {
      this.canvas.style.cursor = this.cursorFor(this.timeAt(event));
      return;
    }

    const time = this.timeAt(event);
    if (this.drag.kind === "move") {
      this.setRegion({ ...this.region, start: time - this.drag.grabOffset });
    } else if (this.drag.kind === "start") {
      // Resizing from the left holds the end still, so the region grows into
      // the track rather than sliding along it.
      const end = regionEnd(this.region);
      const start = Math.min(time, end);
      this.setRegion({ start, length: end - start });
    } else {
      this.setRegion({ ...this.region, length: time - this.region.start });
    }
  }

  private cursorFor(time: number): string {
    const grab = EDGE_GRAB * this.secondsPerPixel();
    if (Math.abs(time - this.region.start) <= grab) return "ew-resize";
    if (Math.abs(time - regionEnd(this.region)) <= grab) return "ew-resize";
    if (time > this.region.start && time < regionEnd(this.region)) return "grab";
    return "crosshair";
  }

  private endDrag(): void {
    if (!this.drag) return;
    // Snap on release rather than during the drag, so the band does not stick
    // to bar lines while the hand is still choosing.
    const start = snapToBar(this.region.start, this.request.beats, this.beatsPerBar);
    this.drag = null;
    this.setRegion({ ...this.region, start });
  }

  private setRegion(next: Region): void {
    this.region = clampRegion(next, this.duration, this.cap);
    this.render();
  }

  private render(): void {
    const canvas = this.canvas;
    if (!fitToDisplay(canvas)) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const key = `${canvas.width}x${canvas.height}`;
    if (this.cacheKey !== key) {
      this.dim = renderOverview(this.request.peaks, canvas.width, canvas.height, THEME.waveDim);
      this.bright = renderOverview(this.request.peaks, canvas.width, canvas.height, THEME.wave);
      this.cacheKey = key;
    }

    ctx.fillStyle = THEME.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (this.dim) ctx.drawImage(this.dim, 0, 0);

    // Where the region falls, in backing-store pixels.
    const scale = this.duration > 0 ? canvas.width / this.duration : 0;
    const left = Math.round(this.region.start * scale);
    const right = Math.round(regionEnd(this.region) * scale);

    if (this.bright) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(left, 0, right - left, canvas.height);
      ctx.clip();
      ctx.drawImage(this.bright, 0, 0);
      ctx.restore();
    }

    // Five-minute marks, so a two-hour file is more than an undifferentiated
    // smear when you are deciding where in it to be.
    ctx.fillStyle = "rgba(255, 255, 255, 0.12)";
    for (let t = GRID_SECONDS; t < this.duration; t += GRID_SECONDS) {
      ctx.fillRect(Math.round(t * scale), 0, 1, canvas.height);
    }

    ctx.fillStyle = "rgba(14, 15, 18, 0.55)";
    ctx.fillRect(0, 0, left, canvas.height);
    ctx.fillRect(right, 0, canvas.width - right, canvas.height);

    ctx.fillStyle = THEME.cue;
    ctx.fillRect(left, 0, 2, canvas.height);
    ctx.fillRect(right - 2, 0, 2, canvas.height);

    this.el(".region-readout").textContent =
      `${formatDuration(this.region.start)} → ${formatDuration(regionEnd(this.region))}` +
      ` · ${formatDuration(this.region.length)} · ` +
      `~${Math.round(decodedBytes(this.region.length) / 1e6)} MB decoded`;
  }

  private close(region: Region | null): void {
    if (this.settled) return;
    this.settled = true;
    document.removeEventListener("keydown", this.onKey, true);
    window.removeEventListener("resize", this.onResize);
    this.root.remove();
    if (this.restoreFocus instanceof HTMLElement) this.restoreFocus.focus();
    this.resolve(region);
  }
}
